/**
 * Payroll module — all sub-pages rendered through a tabbed layout.
 *
 * Tabs: Employees | Pay Components | Structures | Pay Runs | Statutory
 * Each tab maps to a nested route under /payroll/*.
 */
import { useState } from 'react';
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api, type Beneficiary, type BankAccount,
  type EmployeeOut, type PayComponentOut, type SalaryStructureOut,
  type PayslipOut,
  type PayRunSummaryOut, type PayRunDetailOut, type StatutoryConfigOut,
  type EmployeeSalaryOut, type Page,
  type EmploymentType, type TaxRegime, type ComponentType as CType,
  type ComponentCategory, type PayFrequency, type CalcType,
  type PayRunState,
} from '../shared/api';
import {
  Empty, ErrorNote, Modal, PlusIcon, Skeleton,
  formatDate, relativeTime, titleise, useToast,
} from '../shared/ui';

/* ── Payroll shell (tabbed layout) ──────────────────────────────────────── */

const TABS = [
  { to: '/payroll/employees', label: 'Employees' },
  { to: '/payroll/components', label: 'Pay Components' },
  { to: '/payroll/structures', label: 'Structures' },
  { to: '/payroll/runs', label: 'Pay Runs' },
  { to: '/payroll/statutory', label: 'Statutory' },
];

export function PayrollShell() {
  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Payroll</div>
          <h1 className="page-title">Payroll management</h1>
        </div>
      </div>
      <div className="tab-bar" style={{ marginBottom: 16 }}>
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            className={({ isActive }) => `tab${isActive ? ' active' : ''}`}
          >
            {t.label}
          </NavLink>
        ))}
      </div>
      <Outlet />
    </>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   EMPLOYEES
   ═══════════════════════════════════════════════════════════════════════════ */

const EMP_TYPES: EmploymentType[] = ['FULL_TIME', 'PART_TIME', 'CONTRACT', 'INTERN'];
const TAX_REGIMES: TaxRegime[] = ['OLD', 'NEW'];

export function EmployeesPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [showActive, setShowActive] = useState(true);
  const [dept, setDept] = useState('');
  const [page, setPage] = useState(1);

  const qs = new URLSearchParams({ page: String(page), page_size: '25', is_active: String(showActive) });
  if (dept) qs.set('department', dept);

  const { data, isLoading, error } = useQuery({
    queryKey: ['payroll-employees', qs.toString()],
    queryFn: () => api.get<Page<EmployeeOut>>(`/payroll/employees?${qs}`),
  });

  return (
    <>
      <div className="row between row-wrap gap-10" style={{ marginBottom: 14 }}>
        <div className="row gap-10 row-wrap">
          <select
            className="select filter-select" value={String(showActive)}
            onChange={(e) => { setShowActive(e.target.value === 'true'); setPage(1); }}
            aria-label="Status filter"
          >
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <input
            className="input filter-search" placeholder="Filter by department…"
            value={dept} onChange={(e) => { setDept(e.target.value); setPage(1); }}
            aria-label="Department filter"
          />
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <PlusIcon /> Add employee
        </button>
      </div>

      {error && <ErrorNote error={error} />}
      {isLoading ? <Skeleton rows={6} /> : !data || data.items.length === 0 ? (
        <div className="card">
          <Empty
            title="No employees"
            hint="Add employees linked to beneficiaries to start payroll processing."
            action={
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                Add employee
              </button>
            }
          />
        </div>
      ) : (
        <>
          <div className="card card-flush">
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th className="hide-mobile">Designation</th>
                    <th className="hide-mobile">Department</th>
                    <th className="hide-mobile">Type</th>
                    <th className="hide-mobile">Joined</th>
                    <th className="hide-mobile">Tax regime</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((emp) => (
                    <EmployeeRow key={emp.id} emp={emp} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {data.pages > 1 && (
            <div className="row between mt-16">
              <span className="faint" style={{ fontSize: 12.5 }}>
                Page {data.page} of {data.pages} ({data.total} employees)
              </span>
              <div className="row gap-6">
                <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
                <button className="btn btn-ghost btn-sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>Next</button>
              </div>
            </div>
          )}
        </>
      )}

      {showCreate && <CreateEmployee onClose={() => setShowCreate(false)} />}
    </>
  );
}

function EmployeeRow({ emp }: { emp: EmployeeOut }) {
  const navigate = useNavigate();
  return (
    <tr
      className="clickable"
      onClick={() => navigate(`/payroll/employees/${emp.id}`)}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/payroll/employees/${emp.id}`); }}
    >
      <td><span className="mono" style={{ fontSize: 12.5, fontWeight: 600 }}>{emp.employee_code}</span></td>
      <td>{emp.beneficiary_name ?? '—'}</td>
      <td className="hide-mobile faint">{emp.designation ?? '—'}</td>
      <td className="hide-mobile">{emp.department ?? '—'}</td>
      <td className="hide-mobile"><span className="pill pill-draft">{titleise(emp.employment_type)}</span></td>
      <td className="hide-mobile faint" style={{ fontSize: 12.5 }}>{formatDate(emp.date_of_joining)}</td>
      <td className="hide-mobile"><span className="pill pill-info">{emp.tax_regime}</span></td>
    </tr>
  );
}

function CreateEmployee({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();

  const { data: beneficiaries } = useQuery({
    queryKey: ['beneficiaries'],
    queryFn: () => api.get<Beneficiary[]>('/reference/beneficiaries?limit=500'),
  });

  const empBeneficiaries = beneficiaries?.filter((b) => b.kind === 'EMPLOYEE') ?? [];

  const [form, setForm] = useState({
    beneficiary_id: '', employee_code: '', designation: '', department: '',
    employment_type: 'FULL_TIME' as EmploymentType,
    date_of_joining: new Date().toISOString().slice(0, 10),
    pan_last4: '', uan: '', esi_number: '',
    tax_regime: 'NEW' as TaxRegime,
    pf_opted_out: false, esi_applicable: true,
  });

  const set = (field: string, value: unknown) => setForm((p) => ({ ...p, [field]: value }));

  const create = useMutation({
    mutationFn: () => api.post<EmployeeOut>('/payroll/employees', {
      ...form,
      designation: form.designation || null,
      department: form.department || null,
      pan_last4: form.pan_last4 || null,
      uan: form.uan || null,
      esi_number: form.esi_number || null,
    }),
    onSuccess: (emp) => {
      qc.invalidateQueries({ queryKey: ['payroll-employees'] });
      toast(`Employee ${emp.employee_code} created`, 'ok');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Failed to create employee', 'bad'),
  });

  const valid = form.beneficiary_id && form.employee_code && form.date_of_joining;

  return (
    <Modal title="Add employee" onClose={onClose} wide>
      <div className="col gap-14">
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 14 }}>
          <div className="field">
            <label className="label">Beneficiary</label>
            <select className="select" value={form.beneficiary_id} onChange={(e) => set('beneficiary_id', e.target.value)}>
              <option value="">Select employee beneficiary…</option>
              {empBeneficiaries.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.code})</option>)}
            </select>
          </div>
          <div className="field">
            <label className="label">Employee code</label>
            <input className="input" placeholder="EMP001" value={form.employee_code} onChange={(e) => set('employee_code', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Designation</label>
            <input className="input" placeholder="Senior Engineer" value={form.designation} onChange={(e) => set('designation', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Department</label>
            <input className="input" placeholder="Engineering" value={form.department} onChange={(e) => set('department', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Employment type</label>
            <select className="select" value={form.employment_type} onChange={(e) => set('employment_type', e.target.value)}>
              {EMP_TYPES.map((t) => <option key={t} value={t}>{titleise(t)}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="label">Date of joining</label>
            <input className="input" type="date" value={form.date_of_joining} onChange={(e) => set('date_of_joining', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Tax regime</label>
            <select className="select" value={form.tax_regime} onChange={(e) => set('tax_regime', e.target.value)}>
              {TAX_REGIMES.map((t) => <option key={t} value={t}>{t} regime</option>)}
            </select>
          </div>
          <div className="field">
            <label className="label">PAN (last 4)</label>
            <input className="input" placeholder="ABCD" maxLength={4} value={form.pan_last4} onChange={(e) => set('pan_last4', e.target.value.toUpperCase())} />
          </div>
          <div className="field">
            <label className="label">UAN</label>
            <input className="input" placeholder="PF account number" value={form.uan} onChange={(e) => set('uan', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">ESI number</label>
            <input className="input" placeholder="ESI number" value={form.esi_number} onChange={(e) => set('esi_number', e.target.value)} />
          </div>
        </div>

        <div className="row gap-14">
          <label className="row gap-6" style={{ fontSize: 13 }}>
            <input type="checkbox" checked={!form.pf_opted_out} onChange={(e) => set('pf_opted_out', !e.target.checked)} />
            PF applicable
          </label>
          <label className="row gap-6" style={{ fontSize: 13 }}>
            <input type="checkbox" checked={form.esi_applicable} onChange={(e) => set('esi_applicable', e.target.checked)} />
            ESI applicable
          </label>
        </div>

        <div className="row between" style={{ paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          <div />
          <div className="row gap-6">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={!valid || create.isPending} onClick={() => create.mutate()}>
              {create.isPending ? 'Creating…' : 'Add employee'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}


/* ── Employee detail ──────────────────────────────────────────────────── */

export function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [showAssign, setShowAssign] = useState(false);

  const { data: emp, isLoading, error } = useQuery({
    queryKey: ['payroll-employee', id],
    queryFn: () => api.get<EmployeeOut>(`/payroll/employees/${id}`),
    enabled: !!id,
  });

  const { data: salaries } = useQuery({
    queryKey: ['payroll-salaries', id],
    queryFn: () => api.get<EmployeeSalaryOut[]>(`/payroll/salaries/${id}`),
    enabled: !!id,
  });

  const toggle = useMutation({
    mutationFn: () => api.patch<EmployeeOut>(`/payroll/employees/${id}`, { is_active: !emp?.is_active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-employee', id] });
      qc.invalidateQueries({ queryKey: ['payroll-employees'] });
      toast(`Employee ${emp?.is_active ? 'deactivated' : 'reactivated'}`, 'ok');
    },
  });

  if (isLoading) return <Skeleton rows={5} />;
  if (error) return <ErrorNote error={error} />;
  if (!emp) return null;

  return (
    <>
      <div className="page-head">
        <div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/payroll/employees')} style={{ marginBottom: 6 }}>
            &larr; Employees
          </button>
          <h1 className="page-title">{emp.beneficiary_name ?? emp.employee_code}</h1>
          <p className="page-sub">
            {emp.employee_code} &middot; {titleise(emp.employment_type)} &middot; {emp.department ?? 'No department'}
          </p>
        </div>
        <div className="row gap-6">
          <button className="btn btn-primary" onClick={() => setShowAssign(true)}>Assign salary</button>
          <button className={`btn ${emp.is_active ? 'btn-bad' : 'btn-ghost'}`} onClick={() => toggle.mutate()}>
            {emp.is_active ? 'Deactivate' : 'Reactivate'}
          </button>
        </div>
      </div>

      <div className="split">
        <div className="card">
          <div className="card-title" style={{ marginBottom: 14 }}>Profile</div>
          <div className="col gap-10">
            <Field label="Employee code" value={emp.employee_code} />
            <Field label="Designation" value={emp.designation} />
            <Field label="Department" value={emp.department} />
            <Field label="Employment type" value={titleise(emp.employment_type)} />
            <Field label="Date of joining" value={formatDate(emp.date_of_joining)} />
            {emp.date_of_exit && <Field label="Date of exit" value={formatDate(emp.date_of_exit)} />}
            <Field label="Tax regime" value={`${emp.tax_regime} regime`} />
            <Field label="PAN (last 4)" value={emp.pan_last4} />
            <Field label="UAN" value={emp.uan} />
            <Field label="ESI number" value={emp.esi_number} />
            <Field label="PF applicable" value={emp.pf_opted_out ? 'No (opted out)' : 'Yes'} />
            <Field label="ESI applicable" value={emp.esi_applicable ? 'Yes' : 'No'} />
            <Field label="Status" value={emp.is_active ? 'Active' : 'Inactive'} />
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div className="card-title">Salary history</div>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowAssign(true)}><PlusIcon /> Assign</button>
          </div>
          {!salaries || salaries.length === 0 ? (
            <Empty title="No salary assigned" hint="Assign a salary structure and CTC to this employee." />
          ) : (
            <div className="col gap-10">
              {salaries.map((s) => (
                <div key={s.id} className="card" style={{ background: 'var(--ink)', padding: 14 }}>
                  <div className="row between" style={{ marginBottom: 6 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{s.structure_name ?? 'Structure'}</span>
                    <span className="mono" style={{ fontSize: 15, fontWeight: 700 }}>
                      {s.annual_ctc?.formatted ?? '—'}
                    </span>
                  </div>
                  <div className="row gap-10 ghost" style={{ fontSize: 12 }}>
                    <span>From {formatDate(s.effective_from)}</span>
                    {s.effective_to && <span>to {formatDate(s.effective_to)}</span>}
                  </div>
                  {s.note && <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>{s.note}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showAssign && <AssignSalary employeeId={emp.id} onClose={() => setShowAssign(false)} />}
    </>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="row between" style={{ fontSize: 13 }}>
      <span className="faint">{label}</span>
      <span style={{ fontWeight: 500 }}>{value ?? '—'}</span>
    </div>
  );
}

function AssignSalary({ employeeId, onClose }: { employeeId: string; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();

  const { data: structures } = useQuery({
    queryKey: ['payroll-structures'],
    queryFn: () => api.get<SalaryStructureOut[]>('/payroll/structures'),
  });

  const [form, setForm] = useState({
    structure_id: '',
    annual_ctc: '',
    effective_from: new Date().toISOString().slice(0, 10),
    note: '',
  });

  const set = (field: string, value: string) => setForm((p) => ({ ...p, [field]: value }));

  const assign = useMutation({
    mutationFn: () => api.post<EmployeeSalaryOut>('/payroll/salaries', {
      employee_id: employeeId,
      structure_id: form.structure_id,
      annual_ctc_minor: Math.round(Number(form.annual_ctc.replace(/[^\d.]/g, '')) * 100),
      effective_from: form.effective_from,
      note: form.note || null,
      overrides: [],
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-salaries', employeeId] });
      toast('Salary assigned', 'ok');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Failed to assign salary', 'bad'),
  });

  const valid = form.structure_id && Number(form.annual_ctc.replace(/[^\d.]/g, '')) > 0;

  return (
    <Modal title="Assign salary" onClose={onClose}>
      <div className="col gap-14">
        <div className="field">
          <label className="label">Salary structure</label>
          <select className="select" value={form.structure_id} onChange={(e) => set('structure_id', e.target.value)}>
            <option value="">Select structure…</option>
            {structures?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="label">Annual CTC (INR)</label>
          <input className="input" placeholder="600000" inputMode="decimal" value={form.annual_ctc} onChange={(e) => set('annual_ctc', e.target.value)} />
        </div>
        <div className="field">
          <label className="label">Effective from</label>
          <input className="input" type="date" value={form.effective_from} onChange={(e) => set('effective_from', e.target.value)} />
        </div>
        <div className="field">
          <label className="label">Note</label>
          <input className="input" placeholder="Joining package / revision note" value={form.note} onChange={(e) => set('note', e.target.value)} />
        </div>
        <div className="row between" style={{ paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          <div />
          <div className="row gap-6">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={!valid || assign.isPending} onClick={() => assign.mutate()}>
              {assign.isPending ? 'Assigning…' : 'Assign salary'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   PAY COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

const COMP_TYPES: CType[] = ['EARNING', 'DEDUCTION'];
const CATEGORIES: ComponentCategory[] = [
  'BASIC', 'HRA', 'DA', 'SPECIAL', 'CONVEYANCE', 'MEDICAL',
  'LTA', 'BONUS', 'OVERTIME', 'COMMISSION', 'CUSTOM_EARNING',
  'PF_EMPLOYEE', 'PF_EMPLOYER', 'ESI_EMPLOYEE', 'ESI_EMPLOYER',
  'PT', 'TDS', 'CUSTOM_DEDUCTION',
];

export function PayComponentsPage() {
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['payroll-components'],
    queryFn: () => api.get<PayComponentOut[]>('/payroll/components'),
  });

  return (
    <>
      <div className="row between" style={{ marginBottom: 14 }}>
        <p className="page-sub" style={{ margin: 0 }}>
          {data ? `${data.length} component${data.length === 1 ? '' : 's'}` : 'Loading…'}
        </p>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <PlusIcon /> Add component
        </button>
      </div>

      {error && <ErrorNote error={error} />}
      {isLoading ? <Skeleton rows={6} /> : !data || data.length === 0 ? (
        <div className="card">
          <Empty
            title="No pay components"
            hint="Create earnings and deduction components to build salary structures."
            action={<button className="btn btn-primary" onClick={() => setShowCreate(true)}>Add component</button>}
          />
        </div>
      ) : (
        <div className="card card-flush">
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th className="hide-mobile">Category</th>
                  <th className="hide-mobile">Taxable</th>
                  <th className="hide-mobile">Statutory</th>
                  <th className="hide-mobile">% of Basic</th>
                </tr>
              </thead>
              <tbody>
                {data.map((c) => (
                  <tr key={c.id}>
                    <td><span className="mono" style={{ fontSize: 12.5, fontWeight: 600 }}>{c.code}</span></td>
                    <td>{c.name}</td>
                    <td>
                      <span className={`pill ${c.component_type === 'EARNING' ? 'pill-ok' : 'pill-bad'}`}>
                        {c.component_type === 'EARNING' ? '+' : '−'} {titleise(c.component_type)}
                      </span>
                    </td>
                    <td className="hide-mobile faint">{titleise(c.category)}</td>
                    <td className="hide-mobile">{c.is_taxable ? 'Yes' : 'No'}</td>
                    <td className="hide-mobile">{c.is_statutory ? <span className="pill pill-wait">Statutory</span> : '—'}</td>
                    <td className="hide-mobile mono faint">{c.percentage_of_basic != null ? `${c.percentage_of_basic}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreate && <CreateComponent onClose={() => setShowCreate(false)} />}
    </>
  );
}

function CreateComponent({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();

  const [form, setForm] = useState({
    code: '', name: '',
    component_type: 'EARNING' as CType,
    category: 'SPECIAL' as ComponentCategory,
    is_taxable: true, is_statutory: false,
    percentage_of_basic: '',
    sort_order: '0',
  });

  const set = (field: string, value: unknown) => setForm((p) => ({ ...p, [field]: value }));

  const create = useMutation({
    mutationFn: () => api.post<PayComponentOut>('/payroll/components', {
      ...form,
      percentage_of_basic: form.percentage_of_basic ? Number(form.percentage_of_basic) : null,
      sort_order: Number(form.sort_order) || 0,
    }),
    onSuccess: (comp) => {
      qc.invalidateQueries({ queryKey: ['payroll-components'] });
      toast(`Component "${comp.code}" created`, 'ok');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Failed to create component', 'bad'),
  });

  const valid = form.code && form.name;

  return (
    <Modal title="Add pay component" onClose={onClose} wide>
      <div className="col gap-14">
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 14 }}>
          <div className="field">
            <label className="label">Code</label>
            <input className="input" placeholder="BASIC" value={form.code} onChange={(e) => set('code', e.target.value.toUpperCase())} />
          </div>
          <div className="field">
            <label className="label">Name</label>
            <input className="input" placeholder="Basic Salary" value={form.name} onChange={(e) => set('name', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Type</label>
            <select className="select" value={form.component_type} onChange={(e) => set('component_type', e.target.value)}>
              {COMP_TYPES.map((t) => <option key={t} value={t}>{titleise(t)}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="label">Category</label>
            <select className="select" value={form.category} onChange={(e) => set('category', e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{titleise(c)}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="label">% of Basic</label>
            <input className="input" placeholder="12" inputMode="numeric" value={form.percentage_of_basic} onChange={(e) => set('percentage_of_basic', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Sort order</label>
            <input className="input" placeholder="0" inputMode="numeric" value={form.sort_order} onChange={(e) => set('sort_order', e.target.value)} />
          </div>
        </div>
        <div className="row gap-14">
          <label className="row gap-6" style={{ fontSize: 13 }}>
            <input type="checkbox" checked={form.is_taxable} onChange={(e) => set('is_taxable', e.target.checked)} />
            Taxable
          </label>
          <label className="row gap-6" style={{ fontSize: 13 }}>
            <input type="checkbox" checked={form.is_statutory} onChange={(e) => set('is_statutory', e.target.checked)} />
            Statutory
          </label>
        </div>
        <div className="row between" style={{ paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          <div />
          <div className="row gap-6">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={!valid || create.isPending} onClick={() => create.mutate()}>
              {create.isPending ? 'Creating…' : 'Add component'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   SALARY STRUCTURES
   ═══════════════════════════════════════════════════════════════════════════ */

const PAY_FREQ: PayFrequency[] = ['MONTHLY', 'BI_MONTHLY', 'WEEKLY'];
const CALC_TYPES: CalcType[] = ['PERCENT_OF_BASIC', 'PERCENT_OF_CTC', 'FIXED'];

export function StructuresPage() {
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['payroll-structures'],
    queryFn: () => api.get<SalaryStructureOut[]>('/payroll/structures'),
  });

  return (
    <>
      <div className="row between" style={{ marginBottom: 14 }}>
        <p className="page-sub" style={{ margin: 0 }}>
          {data ? `${data.length} structure${data.length === 1 ? '' : 's'}` : 'Loading…'}
        </p>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <PlusIcon /> New structure
        </button>
      </div>

      {error && <ErrorNote error={error} />}
      {isLoading ? <Skeleton rows={4} /> : !data || data.length === 0 ? (
        <div className="card">
          <Empty
            title="No salary structures"
            hint="Create a structure to define how CTC breaks down into components."
            action={<button className="btn btn-primary" onClick={() => setShowCreate(true)}>New structure</button>}
          />
        </div>
      ) : (
        <div className="col gap-14">
          {data.map((s) => (
            <div key={s.id} className="card">
              <div className="row between" style={{ marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{s.name}</div>
                  {s.description && <div className="faint" style={{ fontSize: 12.5, marginTop: 2 }}>{s.description}</div>}
                </div>
                <span className="pill pill-draft">{titleise(s.pay_frequency)}</span>
              </div>

              {s.components.length > 0 ? (
                <div className="table-wrap">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Component</th>
                        <th>Type</th>
                        <th>Calculation</th>
                        <th className="right">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.components.map((c) => (
                        <tr key={c.id}>
                          <td>
                            <span style={{ fontWeight: 500 }}>{c.component_name ?? c.component_code ?? '—'}</span>
                            {c.component_code && <span className="mono ghost" style={{ fontSize: 11, marginLeft: 6 }}>{c.component_code}</span>}
                          </td>
                          <td>
                            <span className={`pill ${c.component_type === 'EARNING' ? 'pill-ok' : 'pill-bad'}`}>
                              {titleise(c.component_type ?? '')}
                            </span>
                          </td>
                          <td className="faint">{titleise(c.calc_type)}</td>
                          <td className="right mono">
                            {c.calc_type === 'FIXED'
                              ? `${(c.value / 100).toFixed(2)}`
                              : `${(c.value / 100).toFixed(2)}%`
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="faint" style={{ fontSize: 13 }}>No components assigned yet.</div>
              )}
            </div>
          ))}
        </div>
      )}

      {showCreate && <CreateStructure onClose={() => setShowCreate(false)} />}
    </>
  );
}

interface DraftStructComp { key: number; component_id: string; calc_type: CalcType; value: string; sort_order: string }

function CreateStructure({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();

  const { data: components } = useQuery({
    queryKey: ['payroll-components'],
    queryFn: () => api.get<PayComponentOut[]>('/payroll/components'),
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [payFrequency, setPayFrequency] = useState<PayFrequency>('MONTHLY');
  const [comps, setComps] = useState<DraftStructComp[]>([]);

  const addComp = () => setComps((p) => [...p, { key: Date.now(), component_id: '', calc_type: 'PERCENT_OF_CTC', value: '', sort_order: String(p.length) }]);
  const patchComp = (key: number, field: string, value: string) =>
    setComps((p) => p.map((c) => c.key === key ? { ...c, [field]: value } : c));

  const create = useMutation({
    mutationFn: () => api.post<SalaryStructureOut>('/payroll/structures', {
      name, description: description || null, pay_frequency: payFrequency,
      components: comps.filter((c) => c.component_id && c.value).map((c) => ({
        component_id: c.component_id,
        calc_type: c.calc_type,
        value: c.calc_type === 'FIXED' ? Math.round(Number(c.value) * 100) : Math.round(Number(c.value) * 100),
        sort_order: Number(c.sort_order) || 0,
      })),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-structures'] });
      toast(`Structure "${name}" created`, 'ok');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Failed to create structure', 'bad'),
  });

  return (
    <Modal title="New salary structure" onClose={onClose} wide>
      <div className="col gap-14">
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 14 }}>
          <div className="field">
            <label className="label">Name</label>
            <input className="input" placeholder="Standard FTE" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Pay frequency</label>
            <select className="select" value={payFrequency} onChange={(e) => setPayFrequency(e.target.value as PayFrequency)}>
              {PAY_FREQ.map((f) => <option key={f} value={f}>{titleise(f)}</option>)}
            </select>
          </div>
        </div>
        <div className="field">
          <label className="label">Description</label>
          <input className="input" placeholder="Describe this structure…" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div>
          <div className="row between" style={{ marginBottom: 8 }}>
            <span className="label">Components</span>
            <button className="btn btn-ghost btn-sm" onClick={addComp}><PlusIcon /> Add</button>
          </div>
          <div className="col gap-6">
            {comps.map((c) => (
              <div key={c.key} className="row gap-6 row-wrap" style={{ background: 'var(--ink)', padding: 9, borderRadius: 8 }}>
                <select className="select grow" style={{ minWidth: 160 }} value={c.component_id} onChange={(e) => patchComp(c.key, 'component_id', e.target.value)}>
                  <option value="">Select component…</option>
                  {components?.map((pc) => <option key={pc.id} value={pc.id}>{pc.name} ({pc.code})</option>)}
                </select>
                <select className="select" style={{ width: 150 }} value={c.calc_type} onChange={(e) => patchComp(c.key, 'calc_type', e.target.value)}>
                  {CALC_TYPES.map((ct) => <option key={ct} value={ct}>{titleise(ct)}</option>)}
                </select>
                <input className="input num" style={{ width: 100 }} placeholder={c.calc_type === 'FIXED' ? '0.00' : '40.00'} inputMode="decimal" value={c.value} onChange={(e) => patchComp(c.key, 'value', e.target.value)} />
                <button className="btn btn-bad btn-sm" onClick={() => setComps((p) => p.filter((x) => x.key !== c.key))}>−</button>
              </div>
            ))}
            {comps.length === 0 && <div className="faint" style={{ fontSize: 13 }}>No components added yet. Click "Add" to start.</div>}
          </div>
          <div className="hint mt-6" style={{ fontSize: 12 }}>
            For percentage types, enter as decimal (e.g. 40.00 for 40%). For fixed, enter the amount per period.
          </div>
        </div>

        <div className="row between" style={{ paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          <div />
          <div className="row gap-6">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={!name || create.isPending} onClick={() => create.mutate()}>
              {create.isPending ? 'Creating…' : 'Create structure'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   PAY RUNS
   ═══════════════════════════════════════════════════════════════════════════ */

const PAY_RUN_STATES: PayRunState[] = ['DRAFT', 'COMPUTING', 'COMPUTED', 'IN_APPROVAL', 'APPROVED', 'PAID', 'CANCELLED'];

const RUN_STATE_PILL: Record<PayRunState, string> = {
  DRAFT: 'pill-draft',
  COMPUTING: 'pill-wait',
  COMPUTED: 'pill-info',
  IN_APPROVAL: 'pill-wait',
  APPROVED: 'pill-ok',
  PAID: 'pill-ok',
  CANCELLED: 'pill-draft',
};

function PayRunStatePill({ state }: { state: PayRunState }) {
  return (
    <span className={`pill ${RUN_STATE_PILL[state]}`}>
      <span className="dot" aria-hidden="true" />
      {titleise(state)}
    </span>
  );
}

export function PayRunsPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [stateFilter, setStateFilter] = useState('');
  const [page, setPage] = useState(1);

  const qs = new URLSearchParams({ page: String(page), page_size: '25' });
  if (stateFilter) qs.set('state', stateFilter);

  const { data, isLoading, error } = useQuery({
    queryKey: ['payroll-runs', qs.toString()],
    queryFn: () => api.get<Page<PayRunSummaryOut>>(`/payroll/runs?${qs}`),
  });

  return (
    <>
      <div className="row between row-wrap gap-10" style={{ marginBottom: 14 }}>
        <select className="select filter-select" value={stateFilter} onChange={(e) => { setStateFilter(e.target.value); setPage(1); }} aria-label="Filter by state">
          <option value="">All states</option>
          {PAY_RUN_STATES.map((s) => <option key={s} value={s}>{titleise(s)}</option>)}
        </select>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <PlusIcon /> New pay run
        </button>
      </div>

      {error && <ErrorNote error={error} />}
      {isLoading ? <Skeleton rows={6} /> : !data || data.items.length === 0 ? (
        <div className="card">
          <Empty
            title="No pay runs"
            hint="Create a pay run to compute salaries for a pay period."
            action={<button className="btn btn-primary" onClick={() => setShowCreate(true)}>New pay run</button>}
          />
        </div>
      ) : (
        <>
          <div className="card card-flush">
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th className="hide-mobile">Period</th>
                    <th className="hide-mobile">Pay date</th>
                    <th className="hide-mobile right">Employees</th>
                    <th className="right">Net pay</th>
                    <th>State</th>
                    <th className="hide-mobile">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((pr) => <PayRunRow key={pr.id} run={pr} />)}
                </tbody>
              </table>
            </div>
          </div>
          {data.pages > 1 && (
            <div className="row between mt-16">
              <span className="faint" style={{ fontSize: 12.5 }}>Page {data.page} of {data.pages}</span>
              <div className="row gap-6">
                <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
                <button className="btn btn-ghost btn-sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>Next</button>
              </div>
            </div>
          )}
        </>
      )}

      {showCreate && <CreatePayRun onClose={() => setShowCreate(false)} />}
    </>
  );
}

function PayRunRow({ run: pr }: { run: PayRunSummaryOut }) {
  const navigate = useNavigate();
  return (
    <tr
      className="clickable"
      onClick={() => navigate(`/payroll/runs/${pr.id}`)}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/payroll/runs/${pr.id}`); }}
    >
      <td style={{ fontWeight: 600, fontSize: 13 }}>{pr.label}</td>
      <td className="hide-mobile faint" style={{ fontSize: 12.5 }}>
        {formatDate(pr.period_start)} – {formatDate(pr.period_end)}
      </td>
      <td className="hide-mobile faint" style={{ fontSize: 12.5 }}>{formatDate(pr.pay_date)}</td>
      <td className="hide-mobile right mono">{pr.employee_count}</td>
      <td className="right mono" style={{ fontWeight: 600 }}>{pr.total_net?.formatted ?? '—'}</td>
      <td><PayRunStatePill state={pr.state} /></td>
      <td className="hide-mobile ghost" style={{ fontSize: 11.5 }}>{relativeTime(pr.created_at)}</td>
    </tr>
  );
}

function CreatePayRun({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);

  const [form, setForm] = useState({
    label: `Salary ${now.toLocaleString('en-IN', { month: 'short', year: 'numeric' })}`,
    period_start: firstDay,
    period_end: lastDay,
    pay_date: lastDay,
    pay_frequency: 'MONTHLY' as PayFrequency,
    note: '',
  });

  const set = (field: string, value: string) => setForm((p) => ({ ...p, [field]: value }));

  const create = useMutation({
    mutationFn: () => api.post<PayRunSummaryOut>('/payroll/runs', {
      ...form, note: form.note || null,
    }),
    onSuccess: (pr) => {
      qc.invalidateQueries({ queryKey: ['payroll-runs'] });
      toast(`Pay run "${pr.label}" created`, 'ok');
      navigate(`/payroll/runs/${pr.id}`);
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Failed to create pay run', 'bad'),
  });

  return (
    <Modal title="New pay run" onClose={onClose} wide>
      <div className="col gap-14">
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 14 }}>
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label className="label">Label</label>
            <input className="input" value={form.label} onChange={(e) => set('label', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Period start</label>
            <input className="input" type="date" value={form.period_start} onChange={(e) => set('period_start', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Period end</label>
            <input className="input" type="date" value={form.period_end} onChange={(e) => set('period_end', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Pay date</label>
            <input className="input" type="date" value={form.pay_date} onChange={(e) => set('pay_date', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Frequency</label>
            <select className="select" value={form.pay_frequency} onChange={(e) => set('pay_frequency', e.target.value)}>
              {PAY_FREQ.map((f) => <option key={f} value={f}>{titleise(f)}</option>)}
            </select>
          </div>
        </div>
        <div className="field">
          <label className="label">Note</label>
          <input className="input" placeholder="Optional note" value={form.note} onChange={(e) => set('note', e.target.value)} />
        </div>
        <div className="row between" style={{ paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          <div />
          <div className="row gap-6">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={!form.label || create.isPending} onClick={() => create.mutate()}>
              {create.isPending ? 'Creating…' : 'Create pay run'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}


/* ── Pay run detail ────────────────────────────────────────────────────── */

export function PayRunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const { data: pr, isLoading, error } = useQuery({
    queryKey: ['payroll-run', id],
    queryFn: () => api.get<PayRunDetailOut>(`/payroll/runs/${id}`),
    enabled: !!id,
  });

  const { data: banks } = useQuery({
    queryKey: ['banks'],
    queryFn: () => api.get<BankAccount[]>('/reference/bank-accounts'),
  });

  const computeMut = useMutation({
    mutationFn: (body: { total_working_days: number }) =>
      api.post<PayRunSummaryOut>(`/payroll/runs/${id}/compute`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-run', id] });
      toast('Pay run computed', 'ok');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Compute failed', 'bad'),
  });

  const approveMut = useMutation({
    mutationFn: () => api.post<PayRunSummaryOut>(`/payroll/runs/${id}/approve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-run', id] });
      toast('Pay run approved', 'ok');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Approve failed', 'bad'),
  });

  const cancelMut = useMutation({
    mutationFn: () => api.post<PayRunSummaryOut>(`/payroll/runs/${id}/cancel`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-run', id] });
      toast('Pay run cancelled', 'ok');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Cancel failed', 'bad'),
  });

  const generateMut = useMutation({
    mutationFn: (bankId: string) =>
      api.post<{ payment_order_id: string; voucher_no: string }>(`/payroll/runs/${id}/generate-order`, { bank_account_id: bankId }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['payroll-run', id] });
      toast(`Payment order ${res.voucher_no} created`, 'ok');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Failed to generate order', 'bad'),
  });

  const [workingDays, setWorkingDays] = useState('30');
  const [selectedPayslip, setSelectedPayslip] = useState<PayslipOut | null>(null);

  if (isLoading) return <Skeleton rows={6} />;
  if (error) return <ErrorNote error={error} />;
  if (!pr) return null;

  const isDraft = pr.state === 'DRAFT';
  const isComputed = pr.state === 'COMPUTED';
  const isApproved = pr.state === 'APPROVED';
  const isTerminal = pr.state === 'PAID' || pr.state === 'CANCELLED';

  return (
    <>
      <div className="page-head">
        <div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/payroll/runs')} style={{ marginBottom: 6 }}>
            &larr; Pay runs
          </button>
          <h1 className="page-title">{pr.label}</h1>
          <p className="page-sub">
            {formatDate(pr.period_start)} – {formatDate(pr.period_end)} &middot; Pay date {formatDate(pr.pay_date)}
          </p>
        </div>
        <PayRunStatePill state={pr.state} />
      </div>

      {/* KPI cards */}
      <div className="grid grid-kpi" style={{ marginBottom: 16 }}>
        <div className="card">
          <div className="stat-label">Employees</div>
          <div className="stat-value">{pr.employee_count}</div>
        </div>
        <div className="card">
          <div className="stat-label">Gross pay</div>
          <div className="stat-value" style={{ fontSize: pr.total_gross?.formatted && pr.total_gross.formatted.length > 11 ? 17 : undefined }}>
            {pr.total_gross?.formatted ?? '—'}
          </div>
        </div>
        <div className="card">
          <div className="stat-label">Deductions</div>
          <div className="stat-value" style={{ color: 'var(--breach)', fontSize: pr.total_deductions?.formatted && pr.total_deductions.formatted.length > 11 ? 17 : undefined }}>
            {pr.total_deductions?.formatted ?? '—'}
          </div>
        </div>
        <div className="card">
          <div className="stat-label">Net pay</div>
          <div className="stat-value" style={{ color: 'var(--verify)', fontSize: pr.total_net?.formatted && pr.total_net.formatted.length > 11 ? 17 : undefined }}>
            {pr.total_net?.formatted ?? '—'}
          </div>
        </div>
      </div>

      {/* Actions */}
      {!isTerminal && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title" style={{ marginBottom: 12 }}>Actions</div>
          <div className="row gap-10 row-wrap">
            {isDraft && (
              <div className="row gap-6">
                <input
                  className="input" style={{ width: 120 }} type="number" min={1} max={31}
                  value={workingDays} onChange={(e) => setWorkingDays(e.target.value)}
                  aria-label="Working days"
                />
                <button
                  className="btn btn-primary"
                  disabled={computeMut.isPending}
                  onClick={() => computeMut.mutate({ total_working_days: Number(workingDays) || 30 })}
                >
                  {computeMut.isPending ? 'Computing…' : 'Compute salaries'}
                </button>
              </div>
            )}
            {isComputed && (
              <button className="btn btn-primary" disabled={approveMut.isPending} onClick={() => approveMut.mutate()}>
                {approveMut.isPending ? 'Approving…' : 'Approve pay run'}
              </button>
            )}
            {isApproved && !pr.payment_order_id && banks && banks.length > 0 && (
              <button
                className="btn btn-primary"
                disabled={generateMut.isPending}
                onClick={() => { const b = banks?.[0]; if (b) generateMut.mutate(b.id); }}
              >
                {generateMut.isPending ? 'Generating…' : 'Generate payment order'}
              </button>
            )}
            {pr.payment_order_id && (
              <button className="btn btn-ghost" onClick={() => navigate(`/orders/${pr.payment_order_id}`)}>
                View payment order
              </button>
            )}
            {!isTerminal && (
              <button className="btn btn-bad" disabled={cancelMut.isPending} onClick={() => cancelMut.mutate()}>
                Cancel run
              </button>
            )}
          </div>
        </div>
      )}

      {/* Payslips */}
      {pr.payslips.length > 0 ? (
        <div className="card card-flush">
          <div className="card-head" style={{ padding: '18px 18px 0' }}>
            <div className="card-title">Payslips ({pr.payslips.length})</div>
          </div>
          <div className="table-wrap" style={{ marginTop: 14 }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th className="hide-mobile">Days</th>
                  <th className="right">Gross</th>
                  <th className="hide-mobile right">Deductions</th>
                  <th className="right">Net pay</th>
                  <th className="hide-mobile">State</th>
                </tr>
              </thead>
              <tbody>
                {pr.payslips.map((ps) => (
                  <tr key={ps.id} className="clickable" onClick={() => setSelectedPayslip(ps)} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setSelectedPayslip(ps); }}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{ps.employee_name ?? '—'}</div>
                      <div className="mono ghost" style={{ fontSize: 11 }}>{ps.employee_code}</div>
                    </td>
                    <td className="hide-mobile mono faint" style={{ fontSize: 12.5 }}>
                      {ps.days_worked}/{ps.total_working_days}
                      {ps.loss_of_pay_days > 0 && <span className="ghost"> (LOP {ps.loss_of_pay_days})</span>}
                    </td>
                    <td className="right mono">{ps.gross?.formatted ?? '—'}</td>
                    <td className="hide-mobile right mono" style={{ color: 'var(--breach)' }}>{ps.total_deductions?.formatted ?? '—'}</td>
                    <td className="right mono" style={{ fontWeight: 600, color: 'var(--verify)' }}>{ps.net_pay?.formatted ?? '—'}</td>
                    <td className="hide-mobile">
                      <span className={`pill ${ps.state === 'FINALIZED' || ps.state === 'PAID' ? 'pill-ok' : ps.state === 'CANCELLED' ? 'pill-bad' : 'pill-draft'}`}>
                        {titleise(ps.state)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card">
          <Empty
            title="No payslips yet"
            hint={isDraft ? 'Compute salaries to generate payslips for all active employees.' : 'This pay run has no payslips.'}
          />
        </div>
      )}

      {pr.note && (
        <div className="card mt-16">
          <div className="card-title" style={{ marginBottom: 8 }}>Note</div>
          <div className="faint" style={{ fontSize: 13 }}>{pr.note}</div>
        </div>
      )}

      {selectedPayslip && (
        <PayslipDetailModal payslip={selectedPayslip} onClose={() => setSelectedPayslip(null)} />
      )}
    </>
  );
}


/* ── Payslip detail modal ─────────────────────────────────────────────── */

function PayslipDetailModal({ payslip: ps, onClose }: { payslip: PayslipOut; onClose: () => void }) {
  const earnings = (ps.earnings ?? []) as Array<Record<string, unknown>>;
  const deductions = (ps.deductions ?? []) as Array<Record<string, unknown>>;
  const employerContribs = (ps.employer_contributions ?? []) as Array<Record<string, unknown>>;
  const taxDetails = ps.tax_details as Record<string, unknown> | null;

  const fmtAmt = (item: Record<string, unknown>) => {
    const amt = item.amount as { formatted?: string; minor?: number } | undefined;
    if (amt?.formatted) return amt.formatted;
    if (typeof amt?.minor === 'number') return `₹${(amt.minor / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    // Fallback: try raw amount field
    if (typeof item.amount === 'number') return `₹${(item.amount / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    return '—';
  };

  const compName = (item: Record<string, unknown>) =>
    (item.component_name ?? item.name ?? item.code ?? 'Component') as string;

  return (
    <Modal title={`${ps.employee_name ?? 'Payslip'} — ${ps.employee_code ?? ''}`} onClose={onClose} wide>
      <div className="col gap-14">
        {/* Summary row */}
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 12 }}>
          <div className="card" style={{ padding: 12 }}>
            <div className="stat-label" style={{ fontSize: 11 }}>Days</div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              {ps.days_worked}/{ps.total_working_days}
            </div>
            {ps.loss_of_pay_days > 0 && (
              <div style={{ fontSize: 11, color: 'var(--breach)' }}>LOP {ps.loss_of_pay_days}</div>
            )}
          </div>
          <div className="card" style={{ padding: 12 }}>
            <div className="stat-label" style={{ fontSize: 11 }}>Gross</div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{ps.gross?.formatted ?? '—'}</div>
          </div>
          <div className="card" style={{ padding: 12 }}>
            <div className="stat-label" style={{ fontSize: 11 }}>Deductions</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--breach)' }}>{ps.total_deductions?.formatted ?? '—'}</div>
          </div>
          <div className="card" style={{ padding: 12 }}>
            <div className="stat-label" style={{ fontSize: 11 }}>Net pay</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--verify)' }}>{ps.net_pay?.formatted ?? '—'}</div>
          </div>
        </div>

        {/* Earnings */}
        {earnings.length > 0 && (
          <div>
            <div className="label" style={{ marginBottom: 8 }}>Earnings</div>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Component</th>
                  <th className="right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {earnings.map((e, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: 13 }}>{compName(e)}</td>
                    <td className="right mono" style={{ fontSize: 13 }}>{fmtAmt(e)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Deductions */}
        {deductions.length > 0 && (
          <div>
            <div className="label" style={{ marginBottom: 8 }}>Deductions</div>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Component</th>
                  <th className="right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {deductions.map((d, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: 13 }}>{compName(d)}</td>
                    <td className="right mono" style={{ fontSize: 13, color: 'var(--breach)' }}>{fmtAmt(d)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Employer contributions */}
        {employerContribs.length > 0 && (
          <div>
            <div className="label" style={{ marginBottom: 8 }}>Employer contributions</div>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Component</th>
                  <th className="right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {employerContribs.map((c, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: 13 }}>{compName(c)}</td>
                    <td className="right mono" style={{ fontSize: 13 }}>{fmtAmt(c)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Employer cost */}
        {ps.employer_cost && (
          <div className="row between" style={{ paddingTop: 10, borderTop: '1px solid var(--line)' }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>Total employer cost (CTC)</span>
            <span className="mono" style={{ fontWeight: 700, fontSize: 14 }}>{ps.employer_cost.formatted}</span>
          </div>
        )}

        {/* Tax details */}
        {taxDetails && Object.keys(taxDetails).length > 0 && (
          <div>
            <div className="label" style={{ marginBottom: 8 }}>Tax details</div>
            <div className="col gap-4">
              {Object.entries(taxDetails).map(([key, val]) => (
                <div key={key} className="row between" style={{ fontSize: 12.5 }}>
                  <span className="faint">{titleise(key)}</span>
                  <span className="mono">
                    {typeof val === 'number'
                      ? `₹${(val / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                      : String(val)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="row between" style={{ paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          <span className={`pill ${ps.state === 'FINALIZED' || ps.state === 'PAID' ? 'pill-ok' : ps.state === 'CANCELLED' ? 'pill-bad' : 'pill-draft'}`}>
            {titleise(ps.state)}
          </span>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </Modal>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   STATUTORY CONFIG
   ═══════════════════════════════════════════════════════════════════════════ */

export function StatutoryPage() {
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['payroll-statutory'],
    queryFn: () => api.get<StatutoryConfigOut[]>('/payroll/statutory'),
  });

  const bpToPercent = (bp: number) => (bp / 100).toFixed(2) + '%';
  const minorToINR = (minor: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(minor / 100);

  return (
    <>
      <div className="row between" style={{ marginBottom: 14 }}>
        <p className="page-sub" style={{ margin: 0 }}>
          {data ? `${data.length} config${data.length === 1 ? '' : 's'}` : 'Loading…'}
        </p>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <PlusIcon /> New config
        </button>
      </div>

      {error && <ErrorNote error={error} />}
      {isLoading ? <Skeleton rows={4} /> : !data || data.length === 0 ? (
        <div className="card">
          <Empty
            title="No statutory config"
            hint="Configure PF, ESI and tax rates for payroll computation."
            action={<button className="btn btn-primary" onClick={() => setShowCreate(true)}>Add config</button>}
          />
        </div>
      ) : (
        <div className="col gap-14">
          {data.map((cfg) => (
            <div key={cfg.id} className="card">
              <div className="row between" style={{ marginBottom: 14 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>
                    Effective from {formatDate(cfg.effective_from)}
                  </div>
                  {cfg.effective_to && <div className="faint" style={{ fontSize: 12 }}>until {formatDate(cfg.effective_to)}</div>}
                </div>
                <span className="ghost" style={{ fontSize: 11 }}>{relativeTime(cfg.created_at)}</span>
              </div>

              <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 16 }}>
                <div>
                  <div className="label" style={{ marginBottom: 8 }}>Provident Fund (PF)</div>
                  <Field label="Employee rate" value={bpToPercent(cfg.pf_employee_rate_bp)} />
                  <Field label="Employer rate" value={bpToPercent(cfg.pf_employer_rate_bp)} />
                  <Field label="Wage ceiling" value={minorToINR(cfg.pf_wage_ceiling_minor)} />
                </div>
                <div>
                  <div className="label" style={{ marginBottom: 8 }}>ESI</div>
                  <Field label="Employee rate" value={bpToPercent(cfg.esi_employee_rate_bp)} />
                  <Field label="Employer rate" value={bpToPercent(cfg.esi_employer_rate_bp)} />
                  <Field label="Wage ceiling" value={minorToINR(cfg.esi_wage_ceiling_minor)} />
                </div>
              </div>

              {cfg.professional_tax && (
                <div style={{ marginTop: 14 }}>
                  <div className="label" style={{ marginBottom: 4 }}>Professional Tax</div>
                  <pre className="mono" style={{ fontSize: 11.5, color: 'var(--faint)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(cfg.professional_tax, null, 2)}
                  </pre>
                </div>
              )}
              {cfg.tds_slabs && (
                <div style={{ marginTop: 14 }}>
                  <div className="label" style={{ marginBottom: 4 }}>TDS Slabs</div>
                  <pre className="mono" style={{ fontSize: 11.5, color: 'var(--faint)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(cfg.tds_slabs, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showCreate && <CreateStatutoryConfig onClose={() => setShowCreate(false)} />}
    </>
  );
}

function CreateStatutoryConfig({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();

  const [form, setForm] = useState({
    effective_from: new Date().toISOString().slice(0, 10),
    pf_employee_rate: '12.00',
    pf_employer_rate: '12.00',
    pf_wage_ceiling: '15000',
    esi_employee_rate: '0.75',
    esi_employer_rate: '3.25',
    esi_wage_ceiling: '21000',
  });

  const set = (field: string, value: string) => setForm((p) => ({ ...p, [field]: value }));

  const create = useMutation({
    mutationFn: () => api.post<StatutoryConfigOut>('/payroll/statutory', {
      effective_from: form.effective_from,
      pf_employee_rate_bp: Math.round(Number(form.pf_employee_rate) * 100),
      pf_employer_rate_bp: Math.round(Number(form.pf_employer_rate) * 100),
      pf_wage_ceiling_minor: Math.round(Number(form.pf_wage_ceiling) * 100),
      esi_employee_rate_bp: Math.round(Number(form.esi_employee_rate) * 100),
      esi_employer_rate_bp: Math.round(Number(form.esi_employer_rate) * 100),
      esi_wage_ceiling_minor: Math.round(Number(form.esi_wage_ceiling) * 100),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-statutory'] });
      toast('Statutory config created', 'ok');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Failed to create config', 'bad'),
  });

  return (
    <Modal title="New statutory config" onClose={onClose} wide>
      <div className="col gap-14">
        <div className="field">
          <label className="label">Effective from</label>
          <input className="input" type="date" value={form.effective_from} onChange={(e) => set('effective_from', e.target.value)} />
        </div>

        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 14 }}>
          <div className="field">
            <label className="label">PF employee rate (%)</label>
            <input className="input" inputMode="decimal" value={form.pf_employee_rate} onChange={(e) => set('pf_employee_rate', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">PF employer rate (%)</label>
            <input className="input" inputMode="decimal" value={form.pf_employer_rate} onChange={(e) => set('pf_employer_rate', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">PF wage ceiling (INR)</label>
            <input className="input" inputMode="decimal" value={form.pf_wage_ceiling} onChange={(e) => set('pf_wage_ceiling', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">ESI employee rate (%)</label>
            <input className="input" inputMode="decimal" value={form.esi_employee_rate} onChange={(e) => set('esi_employee_rate', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">ESI employer rate (%)</label>
            <input className="input" inputMode="decimal" value={form.esi_employer_rate} onChange={(e) => set('esi_employer_rate', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">ESI wage ceiling (INR)</label>
            <input className="input" inputMode="decimal" value={form.esi_wage_ceiling} onChange={(e) => set('esi_wage_ceiling', e.target.value)} />
          </div>
        </div>

        <div className="hint" style={{ fontSize: 12 }}>
          Professional tax slabs and TDS slabs can be configured via the API. Default Indian statutory rates are pre-filled above.
        </div>

        <div className="row between" style={{ paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          <div />
          <div className="row gap-6">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={create.isPending} onClick={() => create.mutate()}>
              {create.isPending ? 'Creating…' : 'Create config'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
